import { entries } from '@debbie/common/dist/util/index.js';
import { RiFilterOffLine } from '@remixicon/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import { ChevronDown, ChevronUp, Filter } from '../Icons/index.js';
import Input from '../Input.js';
import PlaceholderLoader from '../PlaceholderLoader.js';
import { FilterSearchProvider } from './FilterSearch.js';
/**
 * The keys from the anchor row to the clicked one, inclusive, in the order the
 * rows are shown — so a shift-click covers what the eye sees, not the order the
 * underlying data happens to be in.
 *
 * Falls back to the clicked row alone when either end is off screen: the anchor
 * can have been filtered, sorted or paged away since it was set, and there is
 * no range to speak of then.
 */
export const rowsInRange = (visibleKeys, anchorKey, key) => {
    const anchorIndex = anchorKey !== null ? visibleKeys.indexOf(anchorKey) : -1;
    const keyIndex = visibleKeys.indexOf(key);
    if (anchorIndex === -1 || keyIndex === -1) {
        return [key];
    }
    return visibleKeys.slice(Math.min(anchorIndex, keyIndex), Math.max(anchorIndex, keyIndex) + 1);
};
const PowerView = (props) => {
    const lang = useSelector((s) => s.lang);
    // The selection lives with the caller: keeping a second copy here meant a
    // parent clearing `selected` left the boxes ticked
    const selectedProp = props.selectable ? props.selected : undefined;
    const selected = useMemo(() => new Set(selectedProp ?? []), [selectedProp]);
    // The row a range extends from — the last one clicked without shift
    const [anchorKey, setAnchorKey] = useState(null);
    const [openFilter, setOpenFilter] = useState(null);
    const [filterLoc, setFilterLoc] = useState({ x: 0, y: 0 });
    const [filterDirection, setFilterDirection] = useState('left');
    // Check if any filter is active
    const hasActiveFilters = useMemo(() => {
        for (const key in props.values) {
            const value = props.values[key];
            if (value?.filterActive && value.filterActive(props.filter)) {
                return true;
            }
        }
        return false;
    }, [props.values, props.filter]);
    const clearAllFilters = () => {
        if (props.initFilter !== undefined) {
            props.setFilter(props.initFilter);
        }
        else {
            props.setFilter({});
        }
    };
    const values = [];
    for (const key in props.values) {
        if (props.values[key] != undefined) {
            const entry = [key, props.values[key]];
            values.push(entry);
        }
    }
    const showing = props.data;
    const visibleKeys = props.selectable ? showing.map(props.selectKey) : [];
    const selectedVisibleCount = useMemo(() => visibleKeys.filter((key) => selected.has(key)).length, [visibleKeys, selected]);
    // An empty table is not "all selected", however true that is vacuously
    const allSelected = visibleKeys.length > 0 && selectedVisibleCount === visibleKeys.length;
    const someSelected = selectedVisibleCount > 0 && !allSelected;
    // The tri-state of a header checkbox is not expressible in markup
    const selectAllRef = useRef(null);
    useEffect(() => {
        if (selectAllRef.current !== null) {
            selectAllRef.current.indeterminate = someSelected;
        }
    }, [someSelected]);
    const commitSelection = (next) => {
        if (props.selectable) {
            props.onSelect([...next.values()]);
        }
    };
    /**
     * Applies a row's new state to it, or — when shift is held and a row has been
     * clicked before — to every row between the two, the way a file list does.
     * The anchor moves to the clicked row either way, so ranges can be chained.
     */
    const setRowSelected = (key, isSelected, extendFromAnchor) => {
        if (!props.selectable) {
            return;
        }
        const keys = extendFromAnchor
            ? rowsInRange(visibleKeys, anchorKey, key)
            : [key];
        const next = new Set(selected);
        for (const rowKey of keys) {
            if (isSelected) {
                next.add(rowKey);
            }
            else {
                next.delete(rowKey);
            }
        }
        setAnchorKey(key);
        commitSelection(next);
    };
    const selectVisible = () => {
        const next = new Set(selected);
        for (const key of visibleKeys) {
            next.add(key);
        }
        setAnchorKey(null);
        commitSelection(next);
    };
    const deselectVisible = () => {
        const next = new Set(selected);
        for (const key of visibleKeys) {
            next.delete(key);
        }
        setAnchorKey(null);
        commitSelection(next);
    };
    const root = useRef(null);
    const filterRef = useRef(null);
    useEffect(() => {
        const handler = (e) => {
            if (filterRef.current &&
                e.target instanceof Node &&
                !filterRef.current.contains(e.target)) {
                setOpenFilter(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => {
            document.removeEventListener('mousedown', handler);
        };
    }, [filterRef]);
    return (React.createElement(FilterWrapper, null,
        React.createElement(Wrapper, { ref: root },
            React.createElement(Filters, { ref: filterRef, direction: filterDirection, visible: openFilter != null, ...filterLoc },
                React.createElement(FilterSearchProvider, { key: String(openFilter) }, entries(props.values).map(([key, value]) => {
                    if (key !== openFilter || value == null) {
                        return null;
                    }
                    if (value.filter) {
                        return React.createElement(value.filter, {
                            filter: props.filter,
                            setFilter: props.setFilter,
                            key: key,
                        });
                    }
                    return null;
                }))),
            React.createElement("table", { style: { width: '100%' }, cellSpacing: "0", cellPadding: "0" },
                React.createElement(THead, null,
                    React.createElement(Tr, null,
                        props.selectable ? (React.createElement(Th, { selected: false, sortable: false },
                            React.createElement("input", { ref: selectAllRef, type: "checkbox", checked: allSelected, "aria-label": lang.misc.selectAll, onChange: (e) => {
                                    if (e.currentTarget.checked) {
                                        selectVisible();
                                    }
                                    else {
                                        deselectVisible();
                                    }
                                } }))) : null,
                        values.map(([key, val], i) => {
                            const sortable = val.sortable == false ? false : true;
                            const sortKey = val.sortValue ?? key;
                            return (React.createElement(Th, { style: {
                                    width: val.width ? val.width : 'initial',
                                }, key: `${String(key)}${i}`, alignment: val.align, selected: sortKey === props.sortKey, sortable: sortable, onClick: () => {
                                    if (sortable) {
                                        if (props.sortKey === sortKey) {
                                            if (props.direction === 'ASC') {
                                                props.updateSortDirection('DESC');
                                            }
                                            else {
                                                props.updateSortKey(undefined);
                                            }
                                        }
                                        else {
                                            props.updateSortKey(sortKey);
                                            props.updateSortDirection('ASC');
                                        }
                                    }
                                } },
                                React.createElement(HeaderCellContent, null,
                                    React.createElement("div", { style: { display: 'flex', alignItems: 'center' } },
                                        React.createElement("span", { style: { whiteSpace: 'nowrap' } }, val.label),
                                        React.createElement(SortIcon, { sorting: sortKey === props.sortKey }, sortKey === props.sortKey ? (props.direction === 'ASC' ? (React.createElement(ChevronUp, null)) : (React.createElement(ChevronDown, null))) : (''))),
                                    val.filter ? (React.createElement(FilterIcon, { active: val.filterActive
                                            ? val.filterActive(props.filter)
                                            : false, onClick: (e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            if (openFilter === key) {
                                                setOpenFilter(null);
                                                return;
                                            }
                                            setOpenFilter(key);
                                            const bodyRect = root.current.getBoundingClientRect();
                                            const rect = e.currentTarget.offsetParent.getBoundingClientRect();
                                            setFilterDirection(i === 0 ? 'right' : 'left');
                                            setFilterLoc({
                                                x: Math.floor(rect.right - bodyRect.left),
                                                y: Math.floor(rect.bottom - rect.top),
                                            });
                                        } },
                                        React.createElement(Filter, null))) : null)));
                        }),
                        hasActiveFilters && (React.createElement(Th, { selected: false, sortable: false, style: { width: '32px', padding: '0.5em' } },
                            React.createElement(ClearFiltersButton, { onClick: clearAllFilters, "data-tooltip-id": "tooltip-default", "data-tooltip-content": lang.table.clearFilters, "data-tooltip-delay-show": 500 },
                                React.createElement(RiFilterOffLine, { size: 16 })))))),
                React.createElement(TBody, null,
                    props.searchable && (React.createElement(Tr, null,
                        React.createElement(Td, { style: {
                                backgroundColor: THEME.SURFACE_MUTED,
                                borderBottom: `1px solid ${THEME.BORDER_STRONG}`,
                            }, colSpan: values.length +
                                (props.selectable ? 1 : 0) +
                                (hasActiveFilters ? 1 : 0) },
                            React.createElement(Input, { placeholder: lang.table.search, autofocus: props.searchFocused, value: props.searchQuery, onChange: (e) => {
                                    if (props.setSearchQuery) {
                                        props.setSearchQuery(e.target.value);
                                    }
                                }, style: { marginBottom: '0' } })))),
                    !props.dataLoaded && props.loading && (React.createElement(Tr, null,
                        React.createElement(Td, { style: {
                                backgroundColor: THEME.SURFACE_MUTED,
                                borderBottom: `1px solid ${THEME.BORDER_STRONG}`,
                            }, colSpan: values.length +
                                (props.selectable ? 1 : 0) +
                                (hasActiveFilters ? 1 : 0) },
                            React.createElement(PlaceholderLoader, null)))),
                    props.dataLoaded && props.loading ? (React.createElement(Tr, null,
                        React.createElement(Td, { style: {
                                borderBottom: 'none',
                                padding: '0',
                            }, colSpan: values.length +
                                (props.selectable ? 1 : 0) +
                                (hasActiveFilters ? 1 : 0) },
                            React.createElement(PlaceholderLoader, { style: { display: 'block' }, height: "2px" })))) : (React.createElement(Tr, null,
                        React.createElement(Td, { style: {
                                borderBottom: 'none',
                                padding: '0',
                            }, colSpan: values.length +
                                (props.selectable ? 1 : 0) +
                                (hasActiveFilters ? 1 : 0) },
                            React.createElement("div", { style: { height: '2px' } })))),
                    showing.length > 0 ? (showing.map((d) => {
                        return (React.createElement(Tr, { key: props.genKey(d), clickable: typeof props.onClick === 'function' },
                            props.selectable ? (React.createElement(Td, { style: { userSelect: 'none' } },
                                React.createElement("input", { type: "checkbox", checked: selected.has(props.selectKey(d)), onChange: (e) => {
                                        // React routes a checkbox's change through the
                                        // click event, so the modifier keys are on it
                                        const extendFromAnchor = e.nativeEvent instanceof MouseEvent &&
                                            e.nativeEvent.shiftKey;
                                        setRowSelected(props.selectKey(d), e.currentTarget.checked, extendFromAnchor);
                                    } }))) : null,
                            values.map(([key, val], i) => {
                                return (React.createElement(Td, { key: `${String(key)}${i}`, alignment: val.align, onClick: () => {
                                        if (props.onClick) {
                                            props.onClick(d);
                                        }
                                    } }, val.map ? val.map(d[key], d) : d[key]));
                            }),
                            hasActiveFilters && React.createElement(Td, null)));
                    })) : props.dataLoaded ? (React.createElement(Tr, null,
                        React.createElement(Td, { style: {
                                backgroundColor: THEME.SURFACE_MUTED,
                                borderBottom: `1px solid ${THEME.BORDER_STRONG}`,
                                textAlign: 'center',
                            }, colSpan: values.length +
                                (props.selectable ? 1 : 0) +
                                (hasActiveFilters ? 1 : 0) }, props.emptyComponent && props.empty
                            ? React.createElement(props.emptyComponent, {})
                            : lang.table.noData))) : null)))));
};
const FilterWrapper = styled.div `
  position: relative;
`;
const ClearFiltersButton = styled.button `
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0.25em;
  cursor: pointer;
  color: ${THEME.COLOR_ACCENT};
  border-radius: 4px;
  transition: background-color 0.15s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;
const Wrapper = styled.div `
  overflow: auto;
  width: 100%;
`;
const THead = styled.thead `
  tr {
    border-bottom: 1px solid ${THEME.BORDER_STRONG};
  }
`;
const TBody = styled.tbody `
  tr:hover {
    background-color: ${THEME.SURFACE_MUTED};
  }
`;
const Tr = styled.tr `
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'initial')};

  :hover {
  }
`;
const Th = styled.th `
  font-weight: normal;
  padding: 1em;
  border-bottom: 1px solid ${THEME.BORDER_STRONG};
  cursor: ${({ sortable }) => (sortable ? 'pointer' : 'initial')};
  text-align: ${(props) => props.alignment ?? 'left'};
  font-weight: 900;

  color: ${THEME.COLOR_ACCENT};

  &:hover {
    background: ${({ sortable }) => (sortable ? THEME.SURFACE_MUTED : 'none')};
  }
`;
const HeaderCellContent = styled.div `
  display: flex;
  justify-content: space-between;
`;
const Td = styled.td `
  font-weight: normal;
  padding: 1em;
  text-align: ${(props) => props.alignment ?? 'left'};
  border-bottom: 1px solid ${THEME.BORDER};
`;
const SortIcon = styled.span `
  height: 1em;
  width: ${(props) => (props.sorting ? '1em' : '0px')};
  transform: translateY(0.2em);
  display: inline-flex;
  align-items: center;
`;
const FilterIcon = styled.span `
  height: 1em;
  width: 1em;
  transform: translateY(0.2em);
  display: inline-flex;
  align-items: center;

  & polygon {
    fill: ${(p) => (p.active ? THEME.COLOR_ACCENT : 'none')};
  }
`;
const Filters = styled.div `
  display: ${(p) => (p.visible ? 'initial' : 'none')};
  background: #fff;
  box-shadow: 0px 1px 10px 1px rgba(0, 0, 0, 0.1);
  padding: 0.5em;
  position: absolute;
  top: ${(p) => p.y}px;
  left: ${(p) => p.x}px;
  width: 200px;
  &:has([data-searchable-category-filter='true']) {
    width: 320px;
  }
  max-width: calc(100vw - 32px);
  z-index: 10;
  overflow: visible;
  transform: ${(props) => props.direction === 'right'
    ? 'translateX(calc(0% - 36px))'
    : 'translateX(-100%)'};
`;
export default PowerView;
//# sourceMappingURL=View.js.map